"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DynamicQuery = void 0;
const abstract_query_1 = require("../abstract/abstract-query");
/**
 * ### Overview
 * From the official documentation of the OpenSearch project.
 * @see https://opensearch.org/docs/latest/aggregations/
 *
 * Queries consist of query clauses, which can be run in a filter context or query context. A query clause in a filter context asks the question “Does the document match the query clause?” and returns matching documents. A query clause in a query context asks the question “How well does the document match the query clause?”, returns matching documents, and provides the relevance of each document in the form of a relevance score.
 *
 * @description
 * This query builder covers three categories:
 * @see {@link CompoundQuerySchema}
 * - Compound queries that serve as wrappers for multiple leaf or compound clauses either to combine their results or to modify their behavior;
 * @see {@link FullTextQuerySchema}
 * - Full-text queries that uses the standard analyzer, which analyzes text automatically when the query is submitted;
 * @see {@link JoiningQuerySchema}
 * - Joining queries that is used to search nested fields or return parent and child documents that match a specific query;
 * @see {@link TermLevelQuerySchema}
 *  - Term-level queries that search an index for documents that contain an exact search term. Documents returned by a term-level query are not sorted by their relevance scores;
 *
 *  ### Usage
 * ```typescript
 * const queryDSL = new DynamicQuery<DynamicQuerySchema>();
 * queryDSL.addProp('range', { gte: '01-01-2020', lte: '12-31-2020' });
 * queryDSL.generate();
 * ```
 *
 * or
 * ```typescript
 * const queryDSL = new DynamicQuery<DynamicQuerySchema>()
 *  .addProp('range', { gte: '01-01-2020', lte: '12-31-2020' })
 *  .generate();
 * ```
 *
 * ### Overriding / Overloading
 * If you wish to override/overload this class, you may do so by extending this class, and providing any of the schemas mentioned in the @description.
 *
 * For instance,
 * ```typescript
 * export class DynamicQueryOverride extends DynamicQuery<DynamicQuerySchema> {
 *  constructor() {
 *   super();
 *  }
 *
 *  addPropsOverride(..params: any) {
 *    // DO SOMETHING
 *    this.addProps(...params);
 *  }
 * }
 * ```
 * To implement it,
 * ```typescript
 * const queryDSL = new DynamicQueryOverride<CompoundQuerySchema>();
 * queryDSL.addPropsOverride(...args);
 * queryDSL.generate();
 * ```
 * or
 *
 * ```typescript
 * const queryDSL = new DynamicQueryOverride<CompoundQuerySchema>()
 *  .addPropsOverride(...args)
 *  .generate();
 * ```
 */
class DynamicQuery extends abstract_query_1.AbstractQuery {
}
exports.DynamicQuery = DynamicQuery;
